/**
 * Boolean Write Service
 * API service for boolean write button operations
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Global CSRF token cache
let csrfToken: string | null = null;

/**
 * Get CSRF token from backend
 */
const getCSRFToken = async (): Promise<string> => {
  if (csrfToken) return csrfToken;
  
  try {
    const response = await fetch(`${API_BASE_URL}/users/auth/csrf_token/`, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrf_token;
      return csrfToken!;
    }
  } catch (error) {
    console.warn('Failed to get CSRF token:', error);
  }
  
  return '';
};

export interface BooleanWriteResponse {
  success: boolean;
  message?: string;
  error?: string;
  button_id?: number;
  tag_name?: string;
  write_value?: boolean;
  timestamp?: string;
}

class BooleanWriteService {
  private baseUrl = `${API_BASE_URL}/sites`;

  /**
   * Write boolean value using a configured button
   */
  async writeButton(buttonId: number, writeReason?: string): Promise<BooleanWriteResponse> {
    try {
      const token = await getCSRFToken();

      const response = await fetch(`${this.baseUrl}/boolean-write-buttons/${buttonId}/write/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': token,
        },
        credentials: 'include',
        body: JSON.stringify({
          write_reason: writeReason || 'Boolean button write'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`
        };
      }

      return {
        success: true,
        message: data.message,
        button_id: data.button_id,
        tag_name: data.tag_name,
        write_value: data.write_value,
        timestamp: data.timestamp
      };

    } catch (error) {
      console.error('Boolean write failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get boolean write buttons for a site/area
   */
  async getButtons(siteId: number, areaId?: number): Promise<any[]> {
    try {
      let url = `${this.baseUrl}/boolean-write-buttons/by_site/?site_id=${siteId}`;
      if (areaId) {
        url += `&area_id=${areaId}`;
      }

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch buttons: ${response.statusText}`);
      }

      return await response.json();

    } catch (error) {
      console.error('Failed to fetch boolean write buttons:', error);
      return [];
    }
  }
}

export const booleanWriteService = new BooleanWriteService();
